import { Card, Table, useSearchParams, useEffect, useRef } from 'external/react';
import useAdmin from 'context/admin-context/hook';

import { ROW_HEIGHT } from './ClientsTableProperties';
import TableHeader from './ClientsTableHeader';
import TablePaging from './ClientsTablePaging';
import TableToolbar from './ClientsTableToolbar';
import ClientsTableBody from './ClientsTableBody';

const ClientsTable = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAdmin();
  const paging = state.clientsPaging;

  const toolbarRef = useRef<HTMLDivElement>(null);
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const pagingRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const updateParams = (updates: Record<string, string | undefined>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === '') next.delete(key);
        else next.set(key, value);
      });
      return next;
    });
  };

  const calculate = () => {
    const toolbarH = toolbarRef.current?.offsetHeight ?? 0;
    const theadH = theadRef.current?.offsetHeight ?? 0;
    const pagingH = pagingRef.current?.offsetHeight ?? 0;
    const cardPadding = (16 * 2) + 40 + (48 * 2); // Card.Body padding top+bottom, Navbar, Container margin (top+bottom)
    const chrome = toolbarH + theadH + pagingH + cardPadding;
    const available = window.innerHeight - (cardRef.current?.getBoundingClientRect().top ?? 0) - chrome;
    const rows = Math.max(1, Math.floor(available / ROW_HEIGHT));
    updateParams({ pageSize: String(Math.min(rows, 50)), page: '1' });
  };

  useEffect(() => {
    calculate();
    window.addEventListener('resize', calculate);
    return () => window.removeEventListener('resize', calculate);
  }, []);

  const pattern = searchParams.get('pattern') ?? undefined;
  const sortBy = searchParams.get('sortBy') ?? undefined;

  const handleSearch = (pattern: string | undefined) =>
    updateParams({ pattern, page: '1' });

  const handleSort = (sortBy: string) =>
    updateParams({ sortBy: sortBy || undefined, page: '1' });

  const handlePageChange = (page: number) =>
    updateParams({ page: String(page) });

  const noResults = paging && paging.recordCount === 0;

  return (
    <div ref={cardRef}>
      <Card.Body>
        <div ref={toolbarRef}>
          <TableToolbar pattern={pattern} onSearch={handleSearch} />
        </div>

        {noResults ? (
          <div
            className="d-flex flex-column align-items-center justify-content-center text-muted"
            style={{ height: `${ROW_HEIGHT * 5}px` }}
          >
            <span style={{ fontSize: '2rem' }}>🔍</span>
            <p className="mt-2 mb-0">
              No se encontraron clientes{pattern ? ` para "${pattern}"` : ''}.
            </p>
          </div>
        ) : (
          <Table bordered hover style={{ tableLayout: 'fixed', width: '100%' }}>
            <thead ref={theadRef}>
              <TableHeader sortBy={sortBy} onSort={handleSort} />
            </thead>
            <ClientsTableBody />
          </Table>
        )}

        <div ref={pagingRef}>
          {paging && paging.recordCount > 0 && (
            <TablePaging {...paging} onPageChange={handlePageChange} />
          )}
        </div>
      </Card.Body>
    </div>
  );
};

export default ClientsTable;